import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { z } from 'zod';
import { configureServerClock, httpDateSource } from '@etiquetachef/ui-shared/online';
import { auth, functions } from '@/lib/firebase';
import { reportNonFatal } from '@/lib/errorReport';

/**
 * Liga o relógio do servidor do `apps/admin` (hora única do web — #1010).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Por quê: o web não usa a hora do aparelho. Fonte preferida: callable
 * `getServerTime` (ms, exige login); antes do login — e se ela falhar — vale o
 * cabeçalho `Date` do próprio host (1 s). Quando a sessão muda, re-sincroniza
 * para trocar a âncora de 1 s pela de ms. Mesma montagem de `apps/app`.
 *
 * Contrato: importado por efeito em `main.tsx`, antes de renderizar. Falha
 * total e uso do relógio do aparelho antes da âncora vão para a telemetria.
 *
 * Usado por: `main.tsx`; `serverNow()` do ui-shared lê o singleton daqui.
 *
 * Dados — callable `getServerTime`: `{ "now": 1792504800123 }` (epoch ms UTC
 * do servidor), validada com Zod (resposta de callable é fronteira).
 *
 * Issues: #1010, #1009.
 */
const GetServerTimeOutput = z.object({ now: z.number().int().positive() });

const getServerTimeCallable = httpsCallable(functions, 'getServerTime');

export const serverClock = configureServerClock({
  sources: [
    async (): Promise<number> => {
      // Sem sessão a callable falharia (exige login) depois de um RTT inteiro; pula
      // direto para o cabeçalho `Date`. O login re-sincroniza (abaixo) e troca a
      // âncora de 1 s pela de ms. Auditoria global #1010: tirava ~1 RTT do boot.
      if (auth.currentUser === null) throw new Error('no_session');
      const res = await getServerTimeCallable();
      return GetServerTimeOutput.parse(res.data).now;
    },
    httpDateSource('/'),
  ],
  onFallback: (): void => {
    reportNonFatal('server_clock_fallback_device', { where: 'apps/admin' });
  },
  onSyncFailed: (error): void => {
    reportNonFatal('server_clock_sync_failed', { where: 'apps/admin', message: String(error) });
  },
});

onAuthStateChanged(auth, () => {
  void serverClock.sync();
});
